/**
 * La geometría del recorte de foto, y cómo leer la cabecera de un JPEG.
 *
 * Vive separado de `RecorteFoto.tsx` por la misma razón que `calendar-slots.ts`
 * vive separado del calendario: es la única parte de esto que se puede PROBAR.
 * El canvas, el EXIF de verdad y el arrastre no se prueban en `vitest` —corre
 * en `node`, sin DOM y sin canvas—, se verifican en pantalla.
 *
 * ── El desplazamiento va en unidades de la ventana, no en píxeles ───────────
 *
 * `dx`/`dy` se miden en múltiplos del lado de la ventana de recorte, así que el
 * lado se cancela de toda la matemática. Con eso el CSS puede achicar el
 * lienzo en el teléfono, el aparato puede rotarse, y el encuadre no se mueve un
 * píxel. La alternativa —guardar píxeles y tener el lado escrito en el JS—
 * rompe apenas una media query redefine el tamaño: queda una franja de fondo
 * adentro del círculo y se guarda un cuadrado distinto del que se vio.
 *
 * `escala = 1` significa exactamente «la foto cubre la ventana». Definida así,
 * el rango de desplazamiento nunca es vacío y no hay forma geométrica de dejar
 * un hueco — sin un solo caso especial para la foto panorámica, la vertical, ni
 * la más chica que la ventana.
 *
 * ── Por qué NO hay detección de caras ───────────────────────────────────────
 *
 * El encuadre inicial es una REGLA, no un análisis de la imagen, y es a
 * propósito:
 *
 *   · `FaceDetector` no existe en Safari ni en Firefox, y en Chromium vive
 *     detrás de una bandera experimental. Peor: el detector lo pone el sistema
 *     operativo, así que la MISMA foto daría encuadres distintos según el
 *     equipo desde el que se sube. Eso son errores que no se pueden reproducir.
 *   · Una heurística de tono de piel —la regla RGB clásica, o la distancia al
 *     color de referencia de smartcrop— acierta bastante menos en pieles
 *     oscuras, y acá el síntoma sería visible y personal: en la galería del
 *     personal, una persona encuadrada en la camisa mientras las de al lado
 *     quedan en la cara. Sin ese término el modo de falla es aburrido y parejo
 *     para todo el mundo, que es lo que corresponde.
 *
 * Lo que sí se puede afirmar sin mirar los píxeles es la GEOMETRÍA: en una foto
 * vertical la cabeza está arriba. Eso es lo que hace `encuadreInicial`, y es lo
 * que resuelve la queja real (que centrar corta la cabeza). Lo demás lo mueve
 * la persona con la mano, que para eso está el editor.
 */

/** Cómo se enmarca: redonda para personas, cuadrada para un producto. Lo que
 *  se guarda es un cuadrado en los dos casos — la redondez la pone después
 *  `.avatar-img` por CSS. */
export type Forma = 'circulo' | 'cuadrado';

/** El encuadre, normalizado contra el lado de la ventana. */
export interface Encuadre {
  /** 1 = la foto cubre la ventana justo. Nunca menor. */
  escala: number;
  /** Corrimiento del centro de la foto respecto del centro de la ventana. */
  dx: number;
  dy: number;
}

/** Hasta dónde se puede acercar. Tres veces es el punto en que una foto de
 *  teléfono todavía tiene píxeles reales que mostrar en 512. */
export const ESCALA_MAX = 3;

/**
 * El lado del cuadrado que se guarda.
 *
 * El avatar más grande de la app son 72 px CSS (`MyProfile`, `Staff`), y en una
 * pantalla de 3× eso son 216 px reales. 512 deja margen para que mañana alguien
 * lo muestre más grande sin volver a pedirle la foto a nadie, y a esa medida un
 * JPEG de calidad 0,82 pesa entre 40 y 70 KB — dos órdenes de magnitud menos
 * que los 4 MB que hoy ni siquiera se dejaban subir.
 */
export const LADO_SALIDA = 512;

/**
 * Tope al que se decodifica la foto para editarla.
 *
 * No es una preferencia: una foto de 12 MP son ~48 MB de RGBA en memoria, y el
 * teléfono se muere al decodificar, no al recortar. Con 2048 alcanza de sobra
 * para un recorte de 512 aun al máximo acercamiento (2048/3 = 682 > 512).
 */
export const TOPE_DECODIFICACION = 2048;

/** Calidad del JPEG de salida. A 512 px, 0,82 es donde la curva se aplana. */
export const CALIDAD_JPEG = 0.82;

/**
 * Dónde arranca el borde superior del recorte dentro de su recorrido vertical,
 * en una foto vertical de persona.
 *
 * No es 0,5 (centrar) y el motivo es geométrico: en una foto 3:4 el cuadrado
 * solo puede viajar 0,25·H, y centrado cubre de 0,125·H a 0,875·H. La coronilla
 * de un retrato de medio cuerpo vive cerca de 0,05·H — o sea que **centrar
 * corta la cabeza**, que es literalmente la queja del dueño. Con 0,20 el borde
 * superior cae en 0,05·H y la cabeza entra.
 *
 * En foto apaisada y en producto se centra: ahí no hay asimetría que corregir.
 */
const ARRANQUE_VERTICAL = 0.2;

/** Cuánto ocupa la foto pintada, en múltiplos del lado de la ventana. */
export function relativos(w: number, h: number, escala: number): { anchoRel: number; altoRel: number } {
  return {
    anchoRel: escala * Math.max(1, w / h),
    altoRel: escala * Math.max(1, h / w),
  };
}

/** Cuánto se puede correr en cada eje antes de dejar un hueco. */
export function limites(w: number, h: number, escala: number): { lx: number; ly: number } {
  const { anchoRel, altoRel } = relativos(w, h, escala);
  return {
    lx: Math.max(0, (anchoRel - 1) / 2),
    ly: Math.max(0, (altoRel - 1) / 2),
  };
}

/**
 * El encuadre válido más cercano al que se pide.
 *
 * Se aplica en CADA actualización y no al soltar el dedo: recortar al soltar
 * deja ver el hueco justo en el momento en que confunde, y además hace que el
 * arrastre, el pellizco, la rueda, el slider y las flechas diverjan entre sí.
 */
export function acotar(e: Encuadre, w: number, h: number): Encuadre {
  const escala = Math.min(ESCALA_MAX, Math.max(1, e.escala));
  const { lx, ly } = limites(w, h, escala);
  return {
    escala,
    dx: Math.min(lx, Math.max(-lx, e.dx)),
    dy: Math.min(ly, Math.max(-ly, e.dy)),
  };
}

/**
 * Cambiar el acercamiento dejando quieto el punto que está bajo `ancla`
 * (en unidades de lado, relativas al centro de la ventana). `null` es el
 * centro, que es lo que significa mover el slider o apretar «+».
 *
 * Un solo lugar donde se decide qué punto queda quieto: el pellizco, la rueda,
 * el slider y las teclas pasan todos por acá, y por eso no pueden divergir.
 */
export function conEscala(
  e: Encuadre, nueva: number, ancla: { x: number; y: number } | null, w: number, h: number,
): Encuadre {
  const escala = Math.min(ESCALA_MAX, Math.max(1, nueva));
  const k = escala / e.escala;
  const a = ancla ?? { x: 0, y: 0 };
  return acotar({
    escala,
    dx: a.x + (e.dx - a.x) * k,
    dy: a.y + (e.dy - a.y) * k,
  }, w, h);
}

/** El encuadre de arranque: la regla, no un análisis. Ver `ARRANQUE_VERTICAL`. */
export function encuadreInicial(w: number, h: number, forma: Forma): Encuadre {
  const { ly } = limites(w, h, 1);
  const arriba = forma === 'circulo' && h > w;
  return { escala: 1, dx: 0, dy: arriba ? ly * (1 - 2 * ARRANQUE_VERTICAL) : 0 };
}

/**
 * Qué pedazo del bitmap hay que dibujar.
 *
 * Se acota a [0, w − lado] aunque la matemática ya lo garantice: en el tope
 * exacto del arrastre `sx` sale en −0,0000003 por punto flotante, `drawImage`
 * con origen negativo rellena esa columna con el fondo del canvas, y queda una
 * línea de 1 px en el borde del avatar. Se ve más con la rueda y el pellizco
 * que con el slider, que es lo que hace que parezca intermitente.
 */
export function regionFuente(e: Encuadre, w: number, h: number): { sx: number; sy: number; sLado: number } {
  const { anchoRel, altoRel } = relativos(w, h, e.escala);
  const porPx = w / anchoRel;                 // píxeles de la foto por unidad de lado
  const sLado = porPx;
  const cap = (v: number, max: number) => Math.min(Math.max(v, 0), Math.max(0, max));
  return {
    sx: cap((anchoRel / 2 - e.dx - 0.5) * porPx, w - sLado),
    sy: cap((altoRel / 2 - e.dy - 0.5) * porPx, h - sLado),
    sLado,
  };
}

/**
 * Nunca ampliar al guardar: si la región de origen son 300 px, se guarda un
 * JPEG de 300 px. Se ve idéntico —lo agranda el navegador con `object-fit`— y
 * pesa la cuarta parte. Esta es la única garantía de que no se produzca un
 * avatar borroso por haberlo estirado nosotros.
 */
export function ladoSalida(sLado: number): number {
  return Math.max(1, Math.min(LADO_SALIDA, Math.round(sLado)));
}

/* ── La cabecera del JPEG ──────────────────────────────────────────────────── */

/** Lo que se puede saber de un JPEG sin decodificarlo. */
export interface Cabecera {
  ancho: number;
  alto: number;
  /** El valor crudo de `Orientation` del EXIF: 1 = derecha, 6 = girada, etc. */
  orientacion: number;
}

/**
 * Lee el tamaño y la orientación de un JPEG mirando los bytes.
 *
 * Vale las líneas que cuesta porque sirve para dos cosas distintas:
 *
 *   1. Saber cuánto hay que topear ANTES de decodificar. Decodificar primero
 *      para después decidir el tope asigna justamente los 48 MB que el tope
 *      existe para evitar, y el teléfono se muere ahí, no en la segunda pasada.
 *   2. Detectar si el navegador aplicó la orientación EXIF. `imageOrientation`
 *      es un miembro de diccionario: un navegador que no lo conoce **lo ignora
 *      en silencio** y devuelve la foto acostada. No tira, así que ningún
 *      `try/catch` se entera, y la foto vertical del iPhone queda de lado sin
 *      un solo error en ninguna parte.
 *
 * Devuelve `null` si no es un JPEG o si la cabecera viene cortada (se le pasan
 * solo los primeros 128 KB del archivo, no el archivo entero).
 */
export function leerCabeceraJpeg(bytes: Uint8Array): Cabecera | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;

  let orientacion = 1;
  let i = 2;

  while (i + 3 < bytes.length) {
    if (bytes[i] !== 0xff) { i += 1; continue; }      // relleno entre segmentos
    const marca = bytes[i + 1]!;
    if (marca === 0xff) { i += 1; continue; }
    if (marca === 0xd8 || marca === 0x01 || (marca >= 0xd0 && marca <= 0xd7)) { i += 2; continue; }
    if (marca === 0xda || marca === 0xd9) break;      // empezó la imagen: ya no hay cabeceras

    const largo = (bytes[i + 2]! << 8) | bytes[i + 3]!;
    if (largo < 2 || i + 2 + largo > bytes.length) return null;   // cortada

    // SOF0..SOF15, menos los cuatro que no son marcos (C4, C8, CC).
    if (marca >= 0xc0 && marca <= 0xcf && marca !== 0xc4 && marca !== 0xc8 && marca !== 0xcc) {
      const alto = (bytes[i + 5]! << 8) | bytes[i + 6]!;
      const ancho = (bytes[i + 7]! << 8) | bytes[i + 8]!;
      if (ancho > 0 && alto > 0) return { ancho, alto, orientacion };
      return null;
    }

    if (marca === 0xe1) {
      const enc = leerOrientacionExif(bytes, i + 4, largo - 2);
      if (enc != null) orientacion = enc;
    }

    i += 2 + largo;
  }
  return null;
}

/** El `Orientation` (etiqueta 0x0112) del APP1, si está. */
function leerOrientacionExif(bytes: Uint8Array, ini: number, largo: number): number | null {
  const fin = ini + largo;
  if (fin > bytes.length || largo < 14) return null;
  // "Exif\0\0"
  if (bytes[ini] !== 0x45 || bytes[ini + 1] !== 0x78 || bytes[ini + 2] !== 0x69
    || bytes[ini + 3] !== 0x66) return null;

  const tiff = ini + 6;
  const orden = (bytes[tiff]! << 8) | bytes[tiff + 1]!;
  const grande = orden === 0x4d4d;                    // "MM" = big endian
  if (!grande && orden !== 0x4949) return null;

  const u16 = (p: number) => (grande ? (bytes[p]! << 8) | bytes[p + 1]! : (bytes[p + 1]! << 8) | bytes[p]!);
  const u32 = (p: number) => (grande
    ? ((bytes[p]! << 24) | (bytes[p + 1]! << 16) | (bytes[p + 2]! << 8) | bytes[p + 3]!) >>> 0
    : ((bytes[p + 3]! << 24) | (bytes[p + 2]! << 16) | (bytes[p + 1]! << 8) | bytes[p]!) >>> 0);

  const ifd0 = tiff + u32(tiff + 4);
  if (ifd0 + 2 > fin) return null;
  const campos = u16(ifd0);

  for (let n = 0; n < campos; n++) {
    const campo = ifd0 + 2 + n * 12;
    if (campo + 12 > fin) return null;
    if (u16(campo) === 0x0112) {
      const v = u16(campo + 8);
      return v >= 1 && v <= 8 ? v : null;
    }
  }
  return null;
}

/**
 * Cuánto hay que rotar a mano porque el navegador NO aplicó el EXIF.
 *
 * Se compara la proporción que la cabecera predice contra la del bitmap que
 * volvió: si la cabecera dice 200×100 con orientación 6 (girada), la foto
 * derecha es 100×200; si el bitmap sigue midiendo 200×100, el navegador ignoró
 * la opción y hay que rotarla acá.
 *
 * Las orientaciones espejadas (2, 4, 5, 7) se tratan como su rotación sin
 * espejo: vienen de escáneres, no de cámaras, y salen raras pero derechas —
 * que es infinitamente mejor que salir acostadas.
 */
export function rotacionPendiente(cab: Cabecera, anchoBmp: number, altoBmp: number): 0 | 90 | 180 | 270 {
  const giro: Record<number, 0 | 90 | 180 | 270> = {
    1: 0, 2: 0, 3: 180, 4: 180, 5: 90, 6: 90, 7: 270, 8: 270,
  };
  const grados = giro[cab.orientacion] ?? 0;
  if (grados === 0 || anchoBmp <= 0 || altoBmp <= 0) return 0;

  // Con 180° la proporción no cambia, así que no hay forma de saber si el
  // navegador la aplicó: se confía en la cabecera. Con 90/270 sí se puede.
  if (grados === 180) return 180;

  const proporcionBmp = anchoBmp / altoBmp;
  const yaRotada = Math.abs(proporcionBmp - cab.alto / cab.ancho) < Math.abs(proporcionBmp - cab.ancho / cab.alto);
  return yaRotada ? 0 : grados;
}
